import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowLeft, Package, Download, Clock, CheckCircle, Truck, Loader2 } from "lucide-react";
import { API } from "../App";
import Footer from "../components/Footer";

const STATUS_CONFIG = {
  paid: { label: "Order Placed", icon: CheckCircle, color: "text-[#7A9E7E]" },
  shipped: { label: "Shipped", icon: Truck, color: "text-[#A87C6D]" },
  delivered: { label: "Delivered", icon: Package, color: "text-[#7A9E7E]" },
  completed: { label: "Completed", icon: CheckCircle, color: "text-[#7A9E7E]" },
};

export default function Orders({ auth }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await fetch(`${API}/store/orders`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
      }
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#A87C6D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      {/* Header */}
      <header className="border-b border-[#E6E2DD]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#6E6A66] hover:text-[#2D2A26]">
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          
          <h1 
            className="text-xl font-light text-[#2D2A26]"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Order History
          </h1>
          
          <Link to="/store">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-[#E6E2DD] text-[#6E6A66]"
            >
              Shop
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-6 py-8 w-full">
        {orders.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-[#E6E2DD] mx-auto mb-4" />
            <h2 
              className="text-2xl font-light text-[#2D2A26] mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              No orders yet
            </h2>
            <p className="text-[#A8A4A0] mb-6">
              Explore our sensory supports collection.
            </p>
            <Link to="/store">
              <Button className="rounded-full px-8 bg-[#A87C6D] hover:bg-[#956d5f] text-white">
                Browse Products
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map(order => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.paid;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div 
                  key={order.order_id}
                  className="bg-white rounded-2xl border border-[#E6E2DD] overflow-hidden"
                >
                  {/* Order Header */}
                  <div className="px-6 py-4 border-b border-[#E6E2DD] flex flex-wrap items-center justify-between gap-4 bg-[#FDFBF7]">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm text-[#A8A4A0]">Order</p>
                        <p className="font-medium text-[#2D2A26]">#{order.order_id}</p>
                      </div>
                      <div>
                        <p className="text-sm text-[#A8A4A0]">Date</p>
                        <p className="text-[#6E6A66]">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                    
                    <div className={`flex items-center gap-2 ${statusConfig.color}`}>
                      <StatusIcon className="w-5 h-5" />
                      <span className="font-medium">{statusConfig.label}</span>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="px-6 py-4 space-y-3">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-[#2D2A26]">{item.name}</span>
                          <span className="text-[#A8A4A0]">x{item.quantity}</span>
                          {item.product_type === "digital" && (
                            <span className="text-xs px-2 py-0.5 bg-[#A87C6D]/10 text-[#A87C6D] rounded-full">
                              Digital
                            </span>
                          )}
                        </div>
                        <span className="text-[#6E6A66]">
                          ${(item.unit_price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    
                    <div className="pt-3 border-t border-[#E6E2DD] flex justify-between font-medium">
                      <span className="text-[#2D2A26]">Total</span>
                      <span className="text-[#2D2A26]">${order.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Digital Downloads */}
                  {order.has_digital && order.items?.some(i => i.product_type === "digital" && i.digital_download_url) && (
                    <div className="px-6 py-4 bg-[#F5F2ED] border-t border-[#E6E2DD]">
                      <div className="flex items-center gap-2 text-[#A87C6D] mb-3">
                        <Download className="w-5 h-5" />
                        <span className="font-medium">Digital Downloads</span>
                      </div>
                      <div className="space-y-2">
                        {order.items.filter(i => i.product_type === "digital" && i.digital_download_url).map((item, idx) => (
                          <a
                            key={idx}
                            href={item.digital_download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#6E6A66] hover:text-[#A87C6D]"
                          >
                            <Download className="w-4 h-4" />
                            <span>{item.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tracking Info */}
                  {order.has_physical && order.tracking_number && (
                    <div className="px-6 py-4 bg-[#F5F2ED] border-t border-[#E6E2DD]">
                      <div className="flex items-center gap-2 text-[#6E6A66]">
                        <Truck className="w-5 h-5" />
                        <span>Tracking: {order.tracking_number}</span>
                      </div>
                    </div>
                  )}

                  {/* Shipping Address */}
                  {order.has_physical && order.shipping_address && (
                    <div className="px-6 py-4 border-t border-[#E6E2DD]">
                      <p className="text-sm text-[#A8A4A0] mb-1">Shipping to</p>
                      <p className="text-[#6E6A66] text-sm">
                        {order.shipping_address.name}, {order.shipping_address.street}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
